import {AsyncPipe, NgTemplateOutlet} from '@angular/common';
import { Component, OnDestroy, Signal, inject } from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {resetOrderFormDataFormNewOrderPage} from '@app/checkout/state/checkout.actions';
import {ArrayUtils} from '@app/core/utils/array.utils';
import {EProductStatus} from '@app/product/enums/product.status.enum';
import {INewOrderState} from '@app/product/models/new-order-state.model';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {IProduct} from '@app/shared/models/product.model';
import {selectCustomerOrganizationId} from '@app/shared/state/header/header.selector';
import {Store} from '@ngrx/store';
import {TranslatePipe} from '@ngx-translate/core';
import {Bind} from 'primeng/bind';
import {Message} from 'primeng/message';
import {Ripple} from 'primeng/ripple';
import {Skeleton} from 'primeng/skeleton';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Observable, Subject, takeUntil, tap} from 'rxjs';
import {loadProductById, resetProducts} from '../../state/new-order.actions';
import {selectNewOrderState, selectProducts} from '../../state/sign-up.selector';
import {ProductVariantComponent} from '../product-variant/product-variant.component';

@Component({
  selector: 'app-new-order',
  templateUrl: './new-order.component.html',
  styleUrls: ['./new-order.component.scss'],
  imports: [
    Bind,
    Tabs,
    TabList,
    Ripple,
    Tab,
    TabPanels,
    TabPanel,
    ProductVariantComponent,
    Message,
    NgTemplateOutlet,
    Skeleton,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class NewOrderComponent implements OnDestroy {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  newOrderState: Signal<INewOrderState | undefined>;
  products$: Observable<IProduct[] | null>;
  countryId!: number;
  categoryId = 0;
  productId = 0;
  variantId = 0;
  highlightProduct!: boolean;
  private readonly unsubscribe$ = new Subject<void>();
  eActionStatus = EActionStatus;
  getNumberArray = ArrayUtils.getNumberArray;

  constructor() {
    this.route.queryParams?.subscribe(params => {
      if (params['id'] && params['id'] > 0) {
        this.productId = parseInt(params['id']);
        this.variantId = parseInt(params['variantId']) || 0;
        this.store.dispatch(resetOrderFormDataFormNewOrderPage());
        this.store.dispatch(resetProducts());
        this.store
          .select(selectCustomerOrganizationId)
          .pipe(
            takeUntil(this.unsubscribe$),
            tap(customerOrganizationId => {
              if (customerOrganizationId) {
                this.store.dispatch(
                  loadProductById({
                    productId: this.productId,
                    productStatus: EProductStatus.Active,
                  })
                );
              }
            })
          )
          .subscribe();
      }
    });

    this.products$ = this.store.select(selectProducts);
    this.products$
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((products: IProduct[] | null) => {
          if (products?.length) {
            this.categoryId = products[0].productCategories[0].categoryId;
          }
        })
      )
      .subscribe();

    this.newOrderState = toSignal(this.store.select(selectNewOrderState));
  }

  onHighlightProduct(value: boolean) {
    setTimeout(() => {
      this.highlightProduct = value && this.variantId > 0;
      this.scrollToHighlightedVariant();
    }, 500);
  }

  scrollToHighlightedVariant(): void {
    const element = document.querySelector('.search-variant-highlight');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  }

  onBackClicked() {
    this.router.navigate(['/category'], {
      queryParams: {id: this.categoryId},
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
